import React, { useEffect, useRef, useState } from 'react';
import Loading from '../Loading';
import TicketPricingSummary from '../TicketPricingSummary';
import { useForm } from 'react-hook-form';
import { createTransactionService, getCountriesService, getStatesService } from '../../services/transactionsService';
import { getTicketPurchaseOrderDetailsService } from '../../services/ticketPurchaseOrderService';
import { Button, Col, Container, Row } from 'react-bootstrap';

import './index.scss';
import BillingPaymentInformation from '../BillingPaymentInformation';
import { makeBookingPortalPaymentService } from '../../services/paymentService';
import { calculateSurchargeAmount } from '../../utils/ticketPriceCalculations';
import TourConfirmation from '../TourConfirmation';
import moment from 'moment';
import PayrocPaymentModal from '../PaymentFormSections/PayrocPaymentModal';
import { applyPortalTextColor, errorSwal, normalizePaymentError } from '../../utils/helper';
import Swal from 'sweetalert2';
import FiservCardNumberField from '../FiservCardNumberField';
import { useFeatureToggle, FEATURE_TOGGLE_IDS } from '../../utils/hooks/useFeatureToggle';
const TourPaymentLanding = (props) => {
    const ticketPurchaseOrderID = props.match?.params?.ticketPurchaseOrderID;

    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const { register, handleSubmit } = useForm();

    const [selectedTour, setSelectedTour] = useState({});
    const [tripSetting, setTripSetting] = useState({});
    const [tickets, setTickets] = useState([]);
    const [width, setWidth] = useState(window.innerWidth);
    const [errors, setErrors] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState({
        isPaymentConfirmed: false,
    });

    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [fiservToken, setFiservToken] = useState('');
    const [fiservError, setFiservError] = useState(null);
    const [fiservFieldKey, setFiservFieldKey] = useState(0);
    const fiservFieldRef = useRef(null);

    const isFiservHostedFieldsEnabled = useFeatureToggle(FEATURE_TOGGLE_IDS.FISERV_HOSTED_FIELDS);
    const fiservHostedFieldsActive = tripSetting?.paymentProcessorTypeID === 3 && isFiservHostedFieldsEnabled;

    // A charge consumes the token and the expiration/CVV bound to it, so any retry needs freshly entered card details
    const resetFiservCard = () => {
        setFiservToken('');
        setFiservError(null);
        setFiservFieldKey((key) => key + 1);
    };

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const fetchedStates = await getStatesService();
                setStates(fetchedStates?.data);
                const fetchedCountries = await getCountriesService();
                setCountries(fetchedCountries?.data);
                const { data: fetchedTPODetails } = await getTicketPurchaseOrderDetailsService(ticketPurchaseOrderID);
                setSelectedTour(fetchedTPODetails?.tourData);
                applyPortalTextColor(fetchedTPODetails?.accountSettings?.portalTextColor);
                setTripSetting(fetchedTPODetails?.accountSettings);
                setTickets(fetchedTPODetails?.ticketsData);
                setInitialLoading(false);
                if (!fetchedTPODetails || !fetchedTPODetails?.tourData) {
                    await Swal.fire({
                        title: 'Error',
                        text: 'Ticket Purchase Order not found',
                        icon: 'error',
                    });
                    return;
                }
                if (fetchedTPODetails?.tourData?.amountDue === 0) {
                    await Swal.fire({
                        title: 'Payment Status',
                        text: 'You have paid for the tour already.',
                        icon: 'success',
                    });
                    return;
                }
            } catch (error) {
                console.log(error);
                setLoadError(true);
                setInitialLoading(false);
            }
        })();
    }, []);

    const handleShowPaymentModal = (e) => {
        e.preventDefault();
        handleSubmit(async (data) => {
            try {
                const errorArr = [];
                if (!data.email) errorArr.push('email');
                if (!data.street) errorArr.push('street');
                if (!data.city) errorArr.push('city');
                if (!data.state) errorArr.push('state');
                if (!data.zip) errorArr.push('zip');
                setErrors([...errorArr]);

                if (!errorArr.length) {
                    setShowPaymentModal(true);
                }
            } catch (e) {
                console.log(e);
            }
        })();
    };

    const handleSubmitPayment = async (e, token = null) => {
        if (e) e.preventDefault();
        await handleSubmit(async (data) => {
            let chargeAttempted = false;
            try {
                if (selectedTour.amountDue === 0) {
                    setLoading(false);
                    return Swal.fire({
                        title: 'Payment Status',
                        text: 'You have paid for the tour already.',
                        icon: 'success',
                    });
                }
                setLoading(true);

                // Check Payment Data
                const errorArr = [];
                if (!data.email) errorArr.push('email');
                if (!data.street) errorArr.push('street');
                if (!data.city) errorArr.push('city');
                if (!data.state) errorArr.push('state');
                if (!data.zip) errorArr.push('zip');
                if (tripSetting?.paymentProcessorTypeID === 3) {
                    if (!data.firstName) errorArr.push('firstName');
                    if (!data.lastName) errorArr.push('lastName');
                    if (!fiservHostedFieldsActive) {
                        if (!data.cardExpiration) errorArr.push('cardExpiration');
                        if (!data.cardNumber) errorArr.push('cardNumber');
                        if (!data.cvv) errorArr.push('cvv');
                    }
                }

                setErrors([...errorArr]);

                if (!errorArr.length) {
                    const fiservCard = fiservHostedFieldsActive ? await fiservFieldRef.current.getToken() : null;
                    if (fiservCard && !fiservCard.token) {
                        setLoading(false);
                        return Swal.fire({ title: 'Card Details Required', text: fiservCard.message, icon: 'error' });
                    }

                    if (data && data.cardNumber && data.cardNumber.length < 15) {
                        setLoading(false);
                        return Swal.fire({
                            title: 'Card Number Error',
                            text: 'Please enter a valid Card Number',
                            icon: 'error',
                        });
                    }

                    const surchargeAmount = calculateSurchargeAmount({
                        amount: selectedTour.amountDue,
                        tickets,
                        tripSetting,
                    });

                    const transactionRequestData = {
                        ...data,
                        ticketPurchaseOrderID: ticketPurchaseOrderID,
                        amount: Number(selectedTour.amountDue) + Number(surchargeAmount),
                        surchargeAmount: surchargeAmount > 0 ? surchargeAmount : null,
                        transactionTypeID: 3,
                        companyID: tripSetting.companyID,
                        date: moment().format('YYYY-MM-DD'),
                        generalLedgerCodeID: tripSetting.defaultGeneralLedgerCodeID || null,
                    };
                    let transactionRequest = await createTransactionService(transactionRequestData);
                    const { transaction, surchargeTransaction = null } = transactionRequest.data;

                    const paymentRequestData = {
                        ...transactionRequestData,
                        basePaymentAmount: Number(selectedTour.amountDue),
                    };
                    paymentRequestData.companyID = tripSetting.companyID;
                    paymentRequestData.orderID = transaction.id;
                    if (surchargeTransaction) paymentRequestData.surchargeTransactionID = surchargeTransaction.id;
                    if (token) paymentRequestData.token = token;
                    // The tokenizer returns a CardSecure token in place of the PAN; Fiserv takes it in the same field
                    if (fiservCard) paymentRequestData.cardNumber = fiservCard.token;

                    chargeAttempted = true;
                    const response = await makeBookingPortalPaymentService(paymentRequestData, tripSetting.companyID);
                    const paymentResponse = response.data;
                    if (typeof paymentResponse == 'undefined') {
                        Swal.fire({
                            title: 'Transaction Failed',
                            text: "We're Sorry. We have experienced an unknown error. Please wait a few minutes and try again. If the issue persists, please get in touch with us.",
                            icon: 'error',
                        });
                        setLoading(false);
                        return;
                    } else if (paymentResponse.errors && paymentResponse.errors.length > 0) {
                        Swal.fire({
                            title: 'Transaction Declined',
                            text: paymentResponse.errors[0].msg,
                            icon: 'error',
                        });
                        setLoading(false);
                        return;
                    } else if (paymentResponse.error) {
                        Swal.fire({ title: 'Transaction Declined', text: paymentResponse.error, icon: 'error' });
                        setLoading(false);
                        return;
                    } else if (paymentResponse.Result === 'Declined') {
                        Swal.fire({ title: 'Transaction Declined', text: paymentResponse.Status, icon: 'error' });
                        setLoading(false);
                        return;
                    } else if (paymentResponse.code === 'A') {
                        setLoading(false);
                        setPaymentStatus({
                            id: transaction.id,
                            amount: paymentResponse?.amountCharged,
                            isPaymentConfirmed: true,
                            emailSent: paymentResponse.emailSent,
                            surchargeRemoved: paymentResponse?.surchargeRemoved,
                        });
                    }
                }
                setLoading(false);
            } catch (e) {
                if (e.response?.data?.errors?.length) {
                    const normalized = normalizePaymentError(e);
                    await errorSwal({
                        title: 'Payment Failed',
                        text:
                            normalized.message || 'An error occurred while processing your payment. Please try again.',
                    });
                } else {
                    errorSwal({
                        title: 'Payment Failed',
                        text: 'An unexpected error occurred while processing your payment. Please try again.',
                    });
                }
                setLoading(false);
                return;
            } finally {
                if (chargeAttempted && fiservHostedFieldsActive) resetFiservCard();
            }
        })();
    };

    // Submitting overlays the spinner instead of unmounting the form, which would wipe the hosted card field
    return initialLoading || isFiservHostedFieldsEnabled === null ? (
        <Loading loading={true} />
    ) : (
        <div className="background-box-color p-3">
            <div className="background-wrapper" style={{ backgroundColor: `${tripSetting.sectionColor}` }}>
                <Container fluid>
                    {loadError ? (
                        <div className="text-center py-5">
                            <h5>Order Unavailable</h5>
                            <p className="mb-0">
                                This order could not be loaded. Please refresh the page or try again later.
                            </p>
                        </div>
                    ) : paymentStatus.isPaymentConfirmed ? (
                        <TourConfirmation
                            selectedTour={selectedTour}
                            tripSetting={tripSetting}
                            tourContractDescription={selectedTour?.tourContractDescription}
                            formState={paymentStatus}
                        />
                    ) : (
                        <Row>
                            <Col md={8} xs={12} className="d-flex justify-content-center">
                                <div className="w-100 payment-form-main">
                                    <form
                                        className="payment-form-wrapper d-flex space-between flex-column"
                                        onSubmit={(e) => {
                                            handleSubmitPayment(e);
                                        }}
                                    >
                                        <BillingPaymentInformation
                                            register={register}
                                            errors={errors}
                                            setErrors={setErrors}
                                            tripSetting={tripSetting}
                                            states={states}
                                            countries={countries}
                                            isTourOutstandingPayment={true}
                                            hostedCardField={
                                                fiservHostedFieldsActive && (
                                                    <FiservCardNumberField
                                                        key={fiservFieldKey}
                                                        ref={fiservFieldRef}
                                                        onTokenReceived={setFiservToken}
                                                        onErrorReceived={setFiservError}
                                                        companyID={tripSetting.companyID}
                                                        panelColor={tripSetting.mainColor || '#383636'}
                                                        textColor={tripSetting.textColor || '#ffffff'}
                                                        showSecurelyCapturedMessage={!!fiservToken}
                                                        errorMessage={fiservError}
                                                    />
                                                )
                                            }
                                        />

                                        <div className="button-wrapper">
                                            <div className="button-of-first">
                                                <Button
                                                    className="mb-2"
                                                    disabled={
                                                        tickets.some((ticket) => ticket?.errors?.length) ||
                                                        errors.length
                                                    }
                                                    onClick={
                                                        tripSetting.paymentProcessorTypeID === 3
                                                            ? handleSubmitPayment
                                                            : handleShowPaymentModal
                                                    }
                                                    style={{
                                                        backgroundColor: `${loading ? '#646262' : tripSetting.buttonColor}`,
                                                        borderColor: 'transparent',
                                                    }}
                                                >
                                                    <i className="fas fa-lock mr-2 "></i>
                                                    {tripSetting.paymentProcessorTypeID === 3
                                                        ? 'SUBMIT PAYMENT'
                                                        : 'PROCEED TO PAYMENT'}
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </Col>
                            <TicketPricingSummary
                                tripSetting={tripSetting}
                                selectedTour={selectedTour}
                                tickets={tickets}
                                width={width}
                                isTourOutstandingPayment={true}
                            />
                        </Row>
                    )}
                </Container>
                <PayrocPaymentModal
                    show={showPaymentModal}
                    onHide={() => setShowPaymentModal(false)}
                    tripSetting={tripSetting}
                    handleSubmitPayment={handleSubmitPayment}
                    companyId={tripSetting.companyID}
                />
                <Loading loading={loading} />
            </div>
        </div>
    );
};

export default TourPaymentLanding;
